<script lang="ts">
	import Icon from "@components/common/Icon.svelte";
	import { getTranslate } from "@utils/getTranslate.js";
	import { onMount } from "svelte";
	import type { HTMLDialogAttributes } from "svelte/elements";
	import { on } from "svelte/events";
	import plus from "@core/assets/icons/plus.svg?raw";
	import { lockScroll } from "../../state/lockScroll.svelte.js";
	import Heading from "./Heading.svelte";
	import translations from "./Modal.translations.json" with { type: "json" };

	let {
		dialog = $bindable(),
		show = $bindable(false),
		/* eslint-disable prefer-const */
		heading,
		class: customClass,
		...rest
		/* eslint-enable prefer-const */
	}: HTMLDialogAttributes & { show: boolean; heading: string; dialog?: HTMLDialogElement } = $props();

	const translate = getTranslate(translations);

	const componentId = $props.id();

	$effect(() => {
		if (!dialog) {
			return;
		}

		if (show && !dialog.open) {
			dialog.showModal();
		}
		if (!show && dialog.open) {
			dialog.close();
		}
	});

	$effect(() => {
		if (show && !lockScroll.lock) {
			lockScroll.lock = true;
		}
	});
	$effect(() => {
		if (!show) {
			lockScroll.lock = false;
		}
	});

	function closeOnBackdrop(event: MouseEvent): void {
		if (!event.target) {
			return;
		}

		if (event.target === event.currentTarget) {
			show = false;
		}
	}

	// Not using on:click because svelte complains about accessibility (non-interactable elements with a mouse/keyboard event listener).
	onMount(() => dialog && on(dialog, "click", closeOnBackdrop));
</script>

<dialog
	bind:this={dialog}
	{...rest}
	class={[
		"bg-transparent p-4 lg:min-w-96 max-w-full mx-auto backdrop:bg-fcrdarkgray/80 open:flex overflow-auto",
		customClass,
	]}
	onclose={(event: Event & { currentTarget: EventTarget & HTMLDialogElement }): void => {
		show = false;
		rest.onclose?.(event);
	}}
	aria-labelledby="{componentId}-heading"
>
	<!-- This div element makes closing on backdrop work correctly -->
	<div class="bg-white w-full relative py-6 px-3 rounded-3xl flex flex-col overflow-auto">
		<div class="z-10 flex justify-between items-center gap-x-1 shrink-0 px-3">
			<Heading
				id="{componentId}-heading"
				level={2}
				text={heading}
				class="text-fcrdarkgray font-medium leading-[1.2] text-5.5 md:text-8 font-antipol"
			/>
			<button
				type="button"
				class="border-fcrgray-300 border size-12 md:size-15 lg:size-18 flex items-center justify-center rounded-full"
				onclick={(): void => {
					show = false;
				}}
				aria-label={translate("close")}
			>
				<Icon icon={plus} class="size-6 rotate-45" />
			</button>
		</div>

		<div class="flex flex-col md:pb-6 overflow-y-auto px-3">
			{@render rest.children?.()}
		</div>
	</div>
</dialog>

<style>
	@keyframes appear {
		from {
			opacity: 0;
			pointer-events: none;
			transform: translateY(1rem);
		}

		to {
			opacity: 1;
			pointer-events: auto;
			transform: translateY(0);
		}
	}

	dialog {
		animation: appear 300ms ease-out forwards;
	}
</style>
